import { useInspectionTheme } from "@/components/inspection/inspection-theme-context";
import {
  LocalClusteredMarkers,
  MapClusterSummary,
  type MapClusterSummaryState,
} from "@/components/map/map-count-cluster";
import {
  MAP_ZOOM_GESTURE_HANDLING,
  MapZoomHint,
} from "@/components/map/map-zoom-hint";
import {
  INSPECTION_MAP_DEFAULT_CENTER,
  INSPECTION_MAP_DEFAULT_ZOOM,
  INSPECTION_MAP_MAX_ZOOM,
  useInspectionMapFitBounds,
} from "@/hooks/use-inspection-map-bounds";
import {
  isValidThemeHex,
  normalizeThemeHex,
} from "@/lib/inspection-theme-color";
import type { PhotoAnswer } from "@/lib/photo-answer";
import { type PhotoMapPin, buildPhotoMapPins } from "@/lib/photo-map-pins";
import { cn } from "@/lib/utils";
import type { MapCenter } from "@/types/map";
import {
  AdvancedMarker,
  Map as GoogleMap,
  InfoWindow,
  Pin,
} from "@vis.gl/react-google-maps";
import { useCallback, useMemo, useState } from "react";

export type PropertyLocation = {
  latitude: number;
  longitude: number;
};

type PhotoLocationsMapProps = {
  photos: PhotoAnswer[];
  propertyLocation?: PropertyLocation | null;
  className?: string;
  /** Compact height for mobile inspection gallery. */
  compact?: boolean;
};

const DEFAULT_PIN = "#111111";
const DEFAULT_BORDER = "#000000";
const PHOTO_GROUP_PIN = "#2563eb";
const PHOTO_GROUP_BORDER = "#1d4ed8";

function pinColors(themeColor: string | null | undefined): {
  propertyBg: string;
  propertyBorder: string;
} {
  if (themeColor && isValidThemeHex(themeColor)) {
    const hex = normalizeThemeHex(themeColor);
    return { propertyBg: hex, propertyBorder: hex };
  }
  return { propertyBg: DEFAULT_PIN, propertyBorder: DEFAULT_BORDER };
}

/**
 * Compact Google Map showing property + clustered photo capture locations.
 * Used in the inspection portal photo gallery instead of per-photo info boxes.
 */
export function PhotoLocationsMap({
  photos,
  propertyLocation = null,
  className,
  compact = true,
}: PhotoLocationsMapProps) {
  const theme = useInspectionTheme();
  const colors = pinColors(theme?.themeColor);

  const pins = useMemo(
    () =>
      buildPhotoMapPins({
        photos,
        propertyLocation,
      }).map((pin) => ({
        ...pin,
        lat: pin.latitude,
        lng: pin.longitude,
      })),
    [photos, propertyLocation],
  );
  const [clusterSummary, setClusterSummary] =
    useState<MapClusterSummaryState | null>(null);
  const handleClusterSummary = useCallback(
    (summary: MapClusterSummaryState) => {
      setClusterSummary(summary);
    },
    [],
  );

  const mapCenter = useMemo<MapCenter>(() => {
    if (pins.length === 0) return INSPECTION_MAP_DEFAULT_CENTER;
    const lats = pins.map((p) => p.latitude);
    const lngs = pins.map((p) => p.longitude);
    return {
      lat: (Math.min(...lats) + Math.max(...lats)) / 2,
      lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
    };
  }, [pins]);

  if (pins.length === 0) {
    return (
      <p
        className="text-sm text-neutral-600"
        data-testid="photo-locations-empty"
      >
        No photo locations to display.
      </p>
    );
  }

  const heightClass = compact ? "h-40 sm:h-52" : "h-64";

  return (
    <div
      className={cn(
        "relative",
        className ??
          `${heightClass} w-full overflow-hidden rounded-lg border border-neutral-200`,
      )}
      data-testid="photo-locations-map"
    >
      <MapZoomHint />
      <MapClusterSummary
        mode={clusterSummary?.mode ?? "pins"}
        totalInView={clusterSummary?.totalInView ?? pins.length}
        clusterCount={clusterSummary?.clusterCount ?? 0}
      />
      <GoogleMap
        mapId="inspection-photo-locations-map"
        defaultCenter={mapCenter}
        defaultZoom={INSPECTION_MAP_DEFAULT_ZOOM}
        maxZoom={INSPECTION_MAP_MAX_ZOOM}
        gestureHandling={MAP_ZOOM_GESTURE_HANDLING}
        disableDefaultUI={false}
        className="h-full w-full"
      >
        <PhotoLocationsMapBounds pins={pins} />
        <LocalClusteredMarkers
          points={pins}
          onSummary={handleClusterSummary}
          renderPoint={(pin) => (
            <PhotoLocationMarker
              key={pin.id}
              pin={pin}
              propertyBg={colors.propertyBg}
              propertyBorder={colors.propertyBorder}
            />
          )}
        />
      </GoogleMap>
    </div>
  );
}

function PhotoLocationsMapBounds({ pins }: { pins: PhotoMapPin[] }) {
  useInspectionMapFitBounds(pins);
  return null;
}

function PhotoLocationMarker({
  pin,
  propertyBg,
  propertyBorder,
}: {
  pin: PhotoMapPin;
  propertyBg: string;
  propertyBorder: string;
}) {
  const [showInfo, setShowInfo] = useState(false);
  const isProperty = pin.kind === "property";
  const background = isProperty ? propertyBg : PHOTO_GROUP_PIN;
  const borderColor = isProperty ? propertyBorder : PHOTO_GROUP_BORDER;

  return (
    <>
      <AdvancedMarker
        position={{ lat: pin.latitude, lng: pin.longitude }}
        title={pin.popupText}
        onMouseEnter={() => setShowInfo(true)}
        onMouseLeave={() => setShowInfo(false)}
        onClick={() => setShowInfo((open) => !open)}
      >
        <Pin
          background={background}
          borderColor={borderColor}
          glyphColor="#ffffff"
        />
      </AdvancedMarker>
      {showInfo ? (
        <InfoWindow
          position={{ lat: pin.latitude, lng: pin.longitude }}
          disableAutoPan
          headerDisabled
          onCloseClick={() => setShowInfo(false)}
        >
          <div
            className="max-w-[14rem] space-y-0.5 text-sm"
            data-testid={`photo-pin-popup-${pin.id}`}
          >
            {isProperty ? (
              <p className="font-medium text-neutral-900">Property</p>
            ) : null}
            <p>{pin.popupText}</p>
          </div>
        </InfoWindow>
      ) : null}
    </>
  );
}
